import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
    SettingOption,
    IOLinesMode,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import Tibbits from '@appblocks/codegeneration/Tibbits';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';

export default class InterruptNode extends BaseNode {
    key_event: string;

    constructor() {
        super();
        this.name = 'On Interrupt';
        this.id = 'node_interrupt';
        this.linkedFeature = 'general';
        this.requirements = ['tios'];
        this.icon = 'BsLightningFill';

        this.outputs = [
            {
                name: 'Interrupt Triggered',
                portId: 'on_io_int',
                params: [
                    {
                        name: 'intcount',
                        dataType: TibboDataType.WORD,
                        default: '',
                    },
                ],
            },
        ];

        const settings: Setting[] = [
            {
                id: 'INTLINE',
                name: 'Interrupt',
                settingType: SettingType.OPTION,
                required: true,
                default: '',
                options: [],
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getSettingValue(settingId: string): string {
        if (settingId === 'DISPLAY_NAME') {
            const value = super.getSettingValue(settingId);
            if (value !== '') {
                return value;
            }
            const INTLINE = this.getSettingValue('INTLINE');
            if (INTLINE) {
                const feature = this.project.getFeature(INTLINE);
                return `On {{$${feature.getSettingValue('L4NAME')}}} ${this.name.split(' ').pop()}`;
            }
        }
        return super.getSettingValue(settingId);
    }

    getName(): string {
        const INTLINE = this.getSettingValue('INTLINE');
        if (INTLINE) {
            return `On {{$${INTLINE}}} ${this.name.split(' ').pop()}`;
        }
        return this.name;
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        if (settingId === 'INTLINE') {
            return this.project.getInterruptLines();
        }
        return super.getSettingOptions(settingId);
    }

    getCodeBlocks(): CodeBlock[] {
        const blocks: CodeBlock[] = [];
        const project: ProjectConfiguration = this.project;
        const button = this.getSettingValue('BUTTON');
        const links = project.getNodeLinks(this, this.outputs[0].portId);
        const tibbit = project.getFeature(this.getSettingValue('INTLINE'));
        if (!tibbit) {
            return blocks;
        }
        let code = '';
        for (let i = 0; i < links.length; i++) {
            const link = links[i];
            code += `${project.getCodeLocationStart()}${link.getSourceId()}${project.getCodeLocationEnd()}\r\n`;
        }

        const lineName = `${tibbit.getSettingValue('L4NAME')}_num`;
        const socketNumber = tibbit.getSettingValue('SOCKET');


        const interruptNumber = Tibbits.getSocket(
            socketNumber,
            this.project.device.id,
        )?.interrupt;
        if (interruptNumber) {
            const interruptValue = PlatformUtils.getEnumValue(interruptNumber, 'pl_int_num', this.project.device.platform);
            const interruptBit = Math.pow(2, Number(interruptValue));

            blocks.push({
                location: `on_io_int_${interruptNumber}`,
                blockType: BlockType.CODE,
                id: this.getID(),
                template: code,
            });
        }

        return blocks;
    }

    validate(): ValidationMessage[] {
        const errors: ValidationMessage[] = [];

        const INTLINE = this.getSettingValue('INTLINE');

        if (!INTLINE) {
            errors.push({
                message: 'Interrupt line is not set',
                messageType: ValidationMessageType.ERROR,
                featureId: this.getID(),
                settingId: 'INTLINE',
            });
        }

        return errors;
    }

    getDetails(): string {
        return `This block is triggered on interrupt from {{$${this.getSettingValue('INTLINE')}}}`;
    }
}
